import React, { useState, useRef, useEffect } from 'react'
import { Box, styled, IconButton, useTheme, Backdrop, Select, MenuItem, InputLabel, FormControl, Tooltip, Typography, Snackbar, Button } from '@mui/material'

import MicIcon from '@mui/icons-material/Mic';
import CloseIcon from '@mui/icons-material/Close';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import MicOffIcon from '@mui/icons-material/MicOff';
import { useTranslation } from 'react-i18next';
import { recordingSocket, socket } from '../../../config/SocketApi';
import SettingsIcon from '@mui/icons-material/Settings';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import Peer from 'simple-peer';
import InternetSpeedTester from './InternetSpeedTester';
import TutorMeeting from './TutorMeeting';
import { useSelector } from 'react-redux';
import { useAuthContext } from '../../auth/hooks/useAuthContext';
import { getIceConfig } from '../../../config/iceConfig';
import { safeSignalPeer } from '../helpers/realtimeUtils';

const VideoStream = () => {
    const { meetingStarted } = useSelector((store) => store.meeting)
    const { user } = useAuthContext() || JSON.parse(localStorage.getItem("user"));
    const [sessionStarted, setSessionStarted] = useState(false)
    const [socketErrored, setSocketErrored] = useState(false)
    const [tutorStream, setTutorStream] = useState();
    const [attendees, setAttendees] = useState([])
    const [micEnabled, setMicEnabled] = useState(false);
    const [camEnabled, setCamEnabled] = useState(false)
    const [sessionEnded, setSessionEnded] = useState(false);
    const [cameraDevices, setCameraDevices] = useState([])
    const [micDevices, setMicDevices] = useState([])
    const [selectedMicDevice, setSelectedMicDevice] = useState('');
    const [selectedCameraDevice, setSelectedCameraDevice] = useState('');
    const [testAudioStream, setTestAudioStream] = useState();
    const [testVideoStream, setTestVideoStream] = useState();
    const [settingIsOpen, setSettingIsOpen] = useState(false)
    const { t } = useTranslation();
    const theme = useTheme();
    const sessionEndedRef = useRef(false)
    const ControllersBox = styled(Box)({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        flexDirection: 'row',
        gap: '10px',
        height: '50px',
        width: '100%'
    })
    const VideoBox = styled(Box)({
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        width: '100%',
        flex: camEnabled && !meetingStarted ? 6 : undefined,
    })
    const ClassRoomCameraBox = styled(Box)({
        display: camEnabled && !meetingStarted ? 'flex' : 'none',
        // flex: camEnabled && !meetingStarted ? 10 : 0,
        border: `1px solid ${theme.palette.text.secondary}`,
        width: window.innerWidth * 5 / 26 + 10,
        height: (window.innerWidth * 5 / 26) / 1.55 + 10,
        //maxHeight: '200px'
    })
    const MicCameraSelectionBox = styled(Box)({
        display: 'flex',
        position: 'relative',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.text.secondary}`,
        margin: 'auto',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.palette.background.default,
        padding: 20
    })
    const MicRowBox = styled(Box)({
        display: 'flex',
        flexDirection: 'row',
        gap: 4,
        justifyContent: 'flex-start',
        alignItems: 'center'
    })

    const videoRef = useRef()
    const tutorSreamRef = useRef();
    const micCanvasRef = useRef();
    const serverHandlerPeerRef = useRef();
    const cameraVideoRef = useRef();
    const animationFrameRef = useRef(null);
    const senderStudentsPeerRefs = useRef([]);
    const studentsPeerRefs = useRef([])
    const attendeesRef = useRef([])
    const serverPeerRef = useRef();
    const sessionStartedRef = useRef(false);
    const socketErroredRef = useRef(false)
    const answerReceivedRef = useRef(false);
    const currentPeerIdRef = useRef(0);

    const createServerHandlerPeer = (serverHandler, stream) => {
        if (serverHandlerPeerRef.current && !serverHandlerPeerRef.current.destroyed) {
            try {
                serverHandlerPeerRef.current.destroy();
            } catch (error) {
                console.log("[VideoStream] Error destroying previous server-handler peer:", error.message);
            }
            serverHandlerPeerRef.current = null;
        }

        answerReceivedRef.current = false;
        currentPeerIdRef.current = Date.now();
        const peerId = currentPeerIdRef.current;
        let signalCount = 0;
        const newPeer = new Peer({
            initiator: true,
            trickle: true,
            stream,
            config: getIceConfig()
        });
        newPeer._peerId = peerId;
        newPeer.on("signal", (data) => {
            socket.emit("video-stream-signal-to-server-handler", { signal: data, tutor_id: socket.id, handlerSocketId: serverHandler.socket_id, signalCount });
            signalCount++;
        });
        newPeer.on('connect', () => {
            console.log("[VideoStream] Connected to server-handler, peerId:", peerId);
        });
        newPeer.on('error', (err) => {
            // if (!socketErrored && !socketErroredRef.current) createServerHandlerPeer(serverHandler, stream)
            console.log("[VideoStream] Server-handler peer error:", err?.message || err)
        })
        serverHandlerPeerRef.current = newPeer
    }

    useEffect(() => {
        if (!micEnabled && !camEnabled && tutorStream) {
            tutorStream.getTracks().forEach((track) => {
                track.stop();
            });
            socket.emit("server-handler-stop-stream", {})
            setTutorStream(null);
            tutorSreamRef.current = null;
            const tutorAttendee = attendeesRef.current.find(u => u.user_id === user.id)
            tutorAttendee.camEnabled = false;
            tutorAttendee.micEnabled = false;
            setAttendees(attendeesRef.current)
            return;
        }
    }, [micEnabled, camEnabled, tutorStream])


    useEffect(() => {
        if (sessionEnded) {
            if (tutorStream) {
                tutorStream.getTracks().forEach((track) => {
                    track.stop();
                });
            }
            setTutorStream(null);
            tutorSreamRef.current = null;
        }
    }, [sessionEnded])

    useEffect(() => {
        socket.on("server-handler-video-stream-signal", (data) => {
            const signal = data.data;
            const peer = serverHandlerPeerRef.current;
            if (!peer || peer.destroyed) {
                console.log("[VideoStream] No valid server-handler peer to signal");
                return;
            }

            const signalingState = peer._pc?.signalingState;
            if (signal?.type === 'answer') {
                if (answerReceivedRef.current) {
                    console.log("[VideoStream] Ignoring duplicate server-handler answer");
                    return;
                }
                if (signalingState !== 'have-local-offer') {
                    console.log("[VideoStream] Ignoring server-handler answer in state:", signalingState);
                    return;
                }
                answerReceivedRef.current = true;
            }

            const signaled = safeSignalPeer(peer, signal, "VideoStream server-handler");
            if (!signaled && signal?.type === 'answer') {
                answerReceivedRef.current = false;
            }
        })
        socket.on("student-connected-meeting", student => {
            const foundUser = attendeesRef.current.find(a => a.user_id === student.user_id)
            if (foundUser) {
                attendeesRef.current = [...attendeesRef.current.filter(a => a.user_id !== student.user_id), student]
            } else {
                attendeesRef.current = [...attendeesRef.current, student]
            }
            setAttendees(attendeesRef.current)
        })
        socket.on("close-student-mic", (socket_id) => {
            if (senderStudentsPeerRefs.current.length) {
                const disconnectdSenderStudent = senderStudentsPeerRefs.current.find(p => p.sender_student_socket_id === socket_id);
                if (disconnectdSenderStudent) {
                    disconnectdSenderStudent?.peer.destroy();
                    senderStudentsPeerRefs.current = senderStudentsPeerRefs.current.filter(p => p.sender_student_socket_id !== socket_id);
                }
            }
            attendeesRef.current.map(s => {
                if (s.socket_id === socket_id) {
                    if (s.stream) {
                        s.stream.getTracks().forEach((track) => {
                            track.stop();
                        });
                        s.stream = null;
                    }
                }
            })
            setAttendees(attendeesRef.current)
        })

        socket.on("student-stream-signal", (data) => {
            if (data.signal.type === 'offer') {
                const newPeer = new Peer({
                    initiator: false,
                    trickle: true,
                    config: getIceConfig()
                });
                safeSignalPeer(newPeer, data.signal, "VideoStream attendee-offer")
                newPeer.on("stream", (stream) => {
                    // const senderAttendee = attendeesRef.current.find(a => a.socket_id === data.sender_attendee_socket_id)
                    // const senderAttendeeIndex = attendeesRef.current.findIndex(a => a.socket_id === data.sender_attendee_socket_id);
                    // if (senderAttendeeIndex !== -1) {
                    //     attendeesRef.current = attendeesRef.current.map((attendee, index) => {
                    //         if (index === senderAttendeeIndex) {
                    //             return { ...attendee, stream: stream };
                    //         }
                    //         return attendee;
                    //     });
                    // }
                    attendeesRef.current = attendeesRef.current.map(a => {
                        if (a.socket_id === data.sender_attendee_socket_id) {
                            a.stream = stream
                        }
                        return a;
                    })
                    // if (senderAttendee) {
                    //     senderAttendee.stream = stream;
                    //     attendeesRef.current = [...attendeesRef.current.filter(s => s.socket_id !== senderAttendee.socket_id), senderAttendee]
                    // }
                })
                newPeer.on("signal", (signal) => {
                    socket.emit("student-receiver-stream-signal", { signal, sender_attendee_socket_id: data.sender_attendee_socket_id, sender_server_handler_socket_id: data.sender_server_handler_socket_id, peer_socket_id: socket.id });
                });
                newPeer.on("connect", () => {
                    if (attendeesRef.current) setAttendees(attendeesRef.current)
                })
                newPeer.on('error', (err) => { console.log("the peer error is ", err) })
                if (senderStudentsPeerRefs.current.length) {
                    senderStudentsPeerRefs.current = senderStudentsPeerRefs.current.filter(p => p.sender_student_socket_id !== data.sender_attendee_socket_id)
                    senderStudentsPeerRefs.current.push({ peer: newPeer, sender_student_socket_id: data.sender_attendee_socket_id });
                } else {
                    senderStudentsPeerRefs.current.push({ peer: newPeer, sender_student_socket_id: data.sender_attendee_socket_id });
                }
            } else {
                const signal = data.signal;
                const sender_student_socket_id = data.sender_attendee_socket_id;
                if (senderStudentsPeerRefs.current.length) {
                    const studentPeer = senderStudentsPeerRefs.current.find(p => p.sender_student_socket_id === sender_student_socket_id)
                    if (studentPeer) {
                        safeSignalPeer(studentPeer?.peer, signal, "VideoStream attendee-answer");

                    }
                }
            }

        })
        socket.on("server-cam-student-signal", (data) => {
            const signal = data.data;
            if (serverPeerRef.current) {
                safeSignalPeer(serverPeerRef.current, signal, "VideoStream server-cam-student");
            }
        })
        socket.on("cam-server-end-session", () => {
            setSessionEnded(true);
            sessionEndedRef.current = true;
        })
        socket.on("user-disconnected-meeting", (student) => {
            if (student) {
                const disconnectdMeetingStudent = senderStudentsPeerRefs.current.find(p => p.sender_student_socket_id === student.socket_id);
                if (disconnectdMeetingStudent) {
                    disconnectdMeetingStudent?.peer.destroy();
                    senderStudentsPeerRefs.current = senderStudentsPeerRefs.current.filter(p => p.sender_student_socket_id !== student.socket_id);
                }
                attendeesRef.current = attendeesRef.current.filter(a => a.socket_id !== student.socket_id)
                setAttendees(attendeesRef.current)
            }

        })
        recordingSocket.on("connect_error", error => {
            setSocketErrored(true); socketErroredRef.current = true
        })
        recordingSocket.on("connect", attempts => {
            setSocketErrored(false); socketErroredRef.current = false;
        })
        socket.on("teacher-started-session-video-stream", (data) => {
            sessionStartedRef.current = true
            setSessionStarted(true);
        })
        return () => {

            socket.off("student-stream-signal")
            socket.off("student-connected-meeting")
            socket.off("user-disconnected-meeting")
            socket.off("server-handler-video-stream-signal");
            socket.off("cam-server-end-session")
            socket.off("server-cam-student-signal")
            socket.off("close-student-mic")
            socket.off("connect_error");
            socket.off("connect");
            socket.off("teacher-started-session-video-stream")
            if (tutorStream) {
                tutorStream.getTracks().forEach((track) => {
                    track.stop();
                });
            }
        }
    }, [tutorStream])

    useEffect(() => {
        if (attendees.length && !meetingStarted) {
            attendees.map(s_a => {
                const socket_id = s_a.socket_id;
                const foundAudioEl = document.getElementById(socket_id);
                if (foundAudioEl) {
                    foundAudioEl.srcObject = s_a.stream
                } else {
                    var audioEl = document.createElement('audio');
                    audioEl.setAttribute("id", s_a.socket_id)
                    audioEl.controls = false;
                    audioEl.autoplay = true;
                    audioEl.srcObject = s_a.stream;
                    document.body.appendChild(audioEl)

                }
            })
        }
    }, [attendees, meetingStarted])
    const handleMediaSettings = (isopen = true, init = true) => {
        navigator.mediaDevices.enumerateDevices()
            .then(devices => {
                const micList = devices.filter(device => device.kind === 'audioinput');
                const cameraList = devices.filter(device => device.kind === 'videoinput');
                setMicDevices([...new Map(micList.map(item =>
                    [item['groupId'], item])).values()]);
                setCameraDevices(cameraList);
                if ((micList.length > 0 && !selectedMicDevice) || !init) {
                    setSelectedMicDevice([...new Map(micList.map(item =>
                        [item['groupId'], item])).values()][0].deviceId);
                }
                if (cameraList.length > 0 && !selectedCameraDevice) {
                    setSelectedCameraDevice(cameraList[0].deviceId);
                }
                setSettingIsOpen(isopen)
            })
            .catch(error => {
                console.error('Error enumerating media devices:', error);
            });
    }
    const handleChangeMicDevice = (e) => {
        if (testAudioStream) {
            testAudioStream.getTracks().forEach((track) => {
                track.stop();
            });
            setTestAudioStream(null);
        }
        setSelectedMicDevice(e.target.value)
    }
    const handleChangeCamDevice = (e) => {
        if (testVideoStream) {
            testVideoStream.getTracks().forEach((track) => {
                track.stop();
            });
            setTestVideoStream(null);
        }
        setSelectedCameraDevice(e.target.value)
    }
    const visualizeAudio = stream => {

        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        source.connect(analyser);

        const canvas = micCanvasRef.current;
        const ctx = canvas.getContext('2d');
        const WIDTH = canvas.width;
        const HEIGHT = canvas.height;

        analyser.fftSize = 2048;
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const draw = () => {
            animationFrameRef.current = requestAnimationFrame(draw);

            analyser.getByteTimeDomainData(dataArray);

            ctx.fillStyle = theme.palette.background.default;
            ctx.fillRect(0, 0, WIDTH, HEIGHT);

            ctx.lineWidth = 2;
            ctx.strokeStyle = theme.palette.secondary.main;

            ctx.beginPath();

            const sliceWidth = (WIDTH * 1.0) / bufferLength;
            let x = 0;

            for (let i = 0; i < bufferLength; i++) {
                const v = dataArray[i] / 128.0;
                const y = (v * HEIGHT) / 2;
                if (i === 0) {
                    ctx.moveTo(x, y);
                } else {
                    ctx.lineTo(x, y);
                }

                x += sliceWidth;
            }

            ctx.lineTo(canvas.width, canvas.height / 2);
            ctx.stroke();
        };

        draw();
    };
    const handleTestAudio = () => {
        if (testAudioStream) {
            testAudioStream.getTracks().forEach((track) => {
                track.stop();
            });
            setTestAudioStream(null);
            return;
        }
        const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
        const constraints = {
            video: false, // Fallback to true if any of the video constraints are not supported
            audio: supportedConstraints.sampleRate && supportedConstraints.sampleSize && supportedConstraints.channelCount ? {
                sampleRate: 22050,
                sampleSize: 16,
                channelCount: 1,
            } : true // Fallback to true if any of the audio constraints are not supported
        };
        navigator.mediaDevices.getUserMedia(constraints).then(stream => {
            setTestAudioStream(stream);
        })
    }
    const handleTestVideo = () => {
        if (testVideoStream) {
            testVideoStream.getTracks().forEach((track) => {
                track.stop();
            });
            setTestVideoStream(null);
            return;
        }

        navigator.mediaDevices.getUserMedia({
            video: {
                deviceId: selectedCameraDevice,
                width: { max: 360 },  // Reducing resolution to 640px width
                // height: { max: 400 }, // Reducing resolution to 360px height
                frameRate: { max: 10 },
                aspectRatio: { exact: 1.55 }
            }
        }).then(stream => {
            setTestVideoStream(stream);
        })
    }

    useEffect(() => {
        if (testAudioStream) {
            visualizeAudio(testAudioStream)
        }
        if (testVideoStream) {
            cameraVideoRef.current.srcObject = testVideoStream
        }
    }, [testVideoStream, testAudioStream])

    useEffect(() => {
        if (videoRef.current && tutorStream && camEnabled) {
            videoRef.current.srcObject = tutorStream;
        }
    }, [tutorStream, camEnabled, micEnabled, meetingStarted, attendees, sessionStarted, socketErrored, selectedCameraDevice, selectedMicDevice, testAudioStream, testVideoStream, settingIsOpen])

    useEffect(() => {
        if (user) {
            studentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            senderStudentsPeerRefs.current.map(p => { if (p.peer) p.peer.destroy() });
            attendeesRef.current.map(a => {
                if (a.user_id === user.id && a.stream && a.user_type === 0) {
                    a.stream.getTracks().forEach((track) => {
                        track.stop();
                    });
                    socket.emit("close-student-mic", a.user_id)
                }
            })
            attendeesRef.current = []
            socket.emit("get-connected-students-include-sender", students => {
                if (students.length) {
                    attendeesRef.current = students//[...attendeesRef.current.filter(s => s.socket_id !== senderAttendee.socket_id), senderAttendee]
                    setAttendees(students)
                }
            })
        }

    }, [meetingStarted, user])

    useEffect(() => {
        if (navigator.mediaDevices) {
            navigator.mediaDevices.ondevicechange = () => {
                handleMediaSettings(false, false);
            }
        }
    }, [])
    useEffect(() => {
        if (testVideoStream) {
            testVideoStream.getTracks().forEach((track) => {
                track.stop();
            });
            setTestVideoStream(null);
        }
        if (testAudioStream) {
            testAudioStream.getTracks().forEach((track) => {
                track.stop();
            });
            setTestAudioStream(null);
        }
    }, [settingIsOpen])
    useEffect(() => {
        if (socketErrored) {
            socket.emit("kill-video-stream-server-handler", {})
            // if (tutorStream) {
            //     console.log("data ", sessionStarted, tutorStream, socketErrored)
            //     if (serverHandlerPeerRef.current) serverHandlerPeerRef.current.destroy();
            //     serverHandlerPeerRef.current = null
            //     // tutorStream.getTracks().forEach((track) => {
            //     //     track.stop();
            //     // });
            //     // setTutorStream(false)
            //     // setCamEnabled(false);
            //     // setMicEnabled(false)
            // }
        }
        if (sessionStarted && tutorStream && !socketErrored) {
            if (serverHandlerPeerRef.current) serverHandlerPeerRef.current.destroy();
            serverHandlerPeerRef.current = null
            socket.emit("get-server-handler", serverHandler => {
                if (serverHandler) {
                    // const audioTrack = tutorStream.getAudioTracks()[0];
                    // const audioOnlyStream = new MediaStream([audioTrack]);
                    createServerHandlerPeer(serverHandler, tutorStream);
                }
            })


        }
    }, [sessionStarted, tutorStream, socketErrored])

    const handleStreamAudio = () => {
        const tutorAttendee = attendeesRef.current.find(u => u.user_id === user.id)
        if (!sessionEnded) {
            if (tutorStream) {
                tutorStream.getAudioTracks()[0].enabled = !micEnabled;
                setMicEnabled(!micEnabled)
                tutorAttendee.micEnabled = !tutorAttendee.micEnabled;
            } else {
                socket.emit('start-recording', socket.id)
                const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
                const constraints = {
                    video: false, // Fallback to true if any of the video constraints are not supported
                    audio: supportedConstraints.sampleRate && supportedConstraints.sampleSize && supportedConstraints.channelCount ? {
                        sampleRate: 22050,
                        sampleSize: 16,
                        channelCount: 1,
                    } : true // Fallback to true if any of the audio constraints are not supported
                };
                navigator.mediaDevices.getUserMedia(constraints).then(stream => {
                    tutorSreamRef.current = stream;
                    setMicEnabled(true)
                    tutorAttendee.micEnabled = true
                    setTutorStream(stream);

                }).catch(error => console.log(error))
            }
        }
        setAttendees(attendeesRef.current)
    }
    const handleStreamCam = async () => {
        const tutorAttendee = attendeesRef.current.find(u => u.user_id === user.id)
        if (!sessionEnded) {
            if (tutorStream) {
                if (camEnabled) {

                    socket.emit("server-handler-stop-stream", {})
                    tutorSreamRef.current = null;
                    setCamEnabled(false);
                    tutorAttendee.camEnabled = false;
                    const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
                    const constraints = {
                        video: false, // Fallback to true if any of the video constraints are not supported
                        audio: supportedConstraints.sampleRate && supportedConstraints.sampleSize && supportedConstraints.channelCount ? {
                            sampleRate: 22050,
                            sampleSize: 16,
                            channelCount: 1,
                        } : true // Fallback to true if any of the audio constraints are not supported
                    };
                    navigator.mediaDevices.getUserMedia(constraints).then(stream => {
                        socket.emit("tutor-cam-enabled", false)
                        if (tutorStream) {
                            tutorStream.getTracks().forEach((track) => {
                                track.stop();
                            });
                        }
                        setTutorStream(null);
                        tutorSreamRef.current = stream;
                        setTutorStream(stream);
                        setMicEnabled(true)
                        tutorAttendee.micEnabled = true;
                    }).catch(error => console.log(error))

                } else {


                    socket.emit("server-handler-stop-stream", {})
                    tutorSreamRef.current = null;
                    socket.emit('start-recording', socket.id)
                    const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
                    const constraints = {
                        video: supportedConstraints.width && supportedConstraints.frameRate && supportedConstraints.aspectRatio ? {
                            width: { max: 360 },
                            frameRate: { max: 10 },
                            aspectRatio: { exact: 1.55 }
                        } : true, // Fallback to true if any of the video constraints are not supported
                        audio: supportedConstraints.sampleRate && supportedConstraints.sampleSize && supportedConstraints.channelCount ? {
                            sampleRate: 22050,
                            sampleSize: 16,
                            channelCount: 1,
                        } : true // Fallback to true if any of the audio constraints are not supported
                    };
                    navigator.mediaDevices.getUserMedia(constraints).then(stream => {
                        socket.emit("tutor-cam-enabled", true)
                        if (tutorStream) {
                            tutorStream.getTracks().forEach((track) => {
                                track.stop();
                            });
                        }
                        setTutorStream(null);
                        tutorSreamRef.current = stream;
                        setMicEnabled(true);
                        setCamEnabled(true);
                        tutorAttendee.camEnabled = true;
                        tutorAttendee.micEnabled = true;
                        setTutorStream(stream);

                    }).catch(error => console.log(error))
                }

            } else {
                const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
                const constraints = {
                    video: supportedConstraints.width && supportedConstraints.frameRate && supportedConstraints.aspectRatio ? {
                        width: { max: 360 },
                        frameRate: { max: 10 },
                        aspectRatio: { exact: 1.55 }
                    } : true, // Fallback to true if any of the video constraints are not supported
                    audio: supportedConstraints.sampleRate && supportedConstraints.sampleSize && supportedConstraints.channelCount ? {
                        sampleRate: 22050,
                        sampleSize: 16,
                        channelCount: 1,
                    } : true // Fallback to true if any of the audio constraints are not supported
                };
                navigator.mediaDevices.getUserMedia(constraints).then(stream => {
                    socket.emit("tutor-cam-enabled", true)
                    tutorSreamRef.current = stream;
                    setMicEnabled(true);
                    setCamEnabled(true);
                    tutorAttendee.camEnabled = true;
                    tutorAttendee.micEnabled = true;
                    setTutorStream(stream);

                }).catch(error => console.log(error))
            }
        }
        setAttendees(attendeesRef.current)
    }
    return (
        <VideoBox>
            <TutorMeeting tutorStream={tutorStream} attendees={attendees} meetingStarted={meetingStarted} handleAddTutorAudio={() => handleStreamAudio()} handleAddTutorCam={() => handleStreamCam()} />
            <Snackbar
                open={socketErrored}
                autoHideDuration={5000}
                message={t('socketErrored') ?? ('error500')}
                action={
                    <Button color="primary" size="small" onClick={null} sx={{ m: '0px 20px' }}>
                        {t("ok")}
                    </Button>}

            />
            <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={settingIsOpen}
            >
                <MicCameraSelectionBox>
                    <Typography variant='h4' color={theme.palette.text.primary} align='center' margin={2}>{t("cam_mic_settings")}</Typography>
                    <IconButton sx={{ color: theme.palette.danger.main, position: 'absolute', top: 0, left: 0 }} onClick={() => setSettingIsOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                    <MicRowBox>

                        <FormControl variant="standard" sx={{ m: 1, minWidth: 120, width: 500 }}>
                            <InputLabel id="mic-device-selection-label">{t("mic_devices")}</InputLabel>
                            <Select
                                labelId="mic-device-selection-label"
                                id="mic-device-selection"
                                value={selectedMicDevice}
                                onChange={handleChangeMicDevice}
                                label={t("mic_devices")}
                            >
                                {micDevices.map(item =>
                                    (<MenuItem key={item.deciceId} value={item.deviceId}>{item.label}</MenuItem>))}
                            </Select>
                        </FormControl>
                        <Tooltip title={t("test_audio")}>
                            <IconButton sx={{ color: theme.palette.secondary.main }} onClick={handleTestAudio}>
                                {!testAudioStream ? <PlayArrowIcon /> : <StopIcon />}
                            </IconButton>
                        </Tooltip>

                        <canvas ref={micCanvasRef} width="100" height="25" style={{ display: testAudioStream ? 'inline-block' : 'none', }}></canvas>
                    </MicRowBox>
                    <MicRowBox>
                        <FormControl variant="standard" sx={{ m: 1, minWidth: 120, width: 500 }}>
                            <InputLabel id="cam-device-selection-label">{t("cam_devices")}</InputLabel>
                            <Select
                                labelId="cam-device-selection-label"
                                id="cam-device-selection"
                                value={selectedCameraDevice}
                                onChange={handleChangeCamDevice}
                                label={t("cam_devices")}
                            >
                                {cameraDevices.map(md => (<MenuItem key={md.deviceId} value={md.deviceId}>{md.label}</MenuItem>))}
                            </Select>
                        </FormControl>
                        <Tooltip title={t("test_video")}>
                            <IconButton sx={{ color: theme.palette.secondary.main }} onClick={handleTestVideo}>
                                {!testVideoStream ? <PlayArrowIcon /> : <StopIcon />}
                            </IconButton>
                        </Tooltip>


                    </MicRowBox>
                    <video ref={cameraVideoRef} width={400} height={350} autoPlay muted style={{ display: testVideoStream ? 'inline-block' : 'none', }}></video>
                </MicCameraSelectionBox>
            </Backdrop>
            <ControllersBox>
                <InternetSpeedTester />
                <IconButton sx={{ color: theme.palette.secondary.main }} onClick={() => handleStreamAudio()}>
                    {micEnabled ? (<MicOffIcon />) : (<MicIcon />)}
                </IconButton>
                <IconButton sx={{ color: theme.palette.secondary.main }} onClick={() => handleStreamCam()}>
                    {camEnabled ? (<VideocamOffIcon />) : (<VideoCameraFrontIcon />)}
                </IconButton>
                <IconButton sx={{ color: theme.palette.secondary.main }} onClick={handleMediaSettings}>
                    <SettingsIcon />
                </IconButton>
            </ControllersBox>
            <ClassRoomCameraBox >
                <video id='live_stream_video' ref={videoRef} width={window.innerWidth * 5 / 26 + 8} height={(window.innerWidth * 5 / 26) / 1.55 + 8} autoPlay muted style={{ display: camEnabled && !meetingStarted ? 'inline-block' : 'none', }} />
            </ClassRoomCameraBox>
        </VideoBox>
    )
}

export default VideoStream
